import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * useSafeBack
 * -----------
 * Returns a back-navigation handler that prefers the browser history
 * (so the user returns to the *previous* in-app screen they actually
 * came from) but falls back to a safe parent path when there's no
 * in-app history to pop — e.g. direct deep-link entry, a freshly
 * refreshed tab, an OAuth bounce, or a notification-launched session.
 *
 * Why this matters for HealthLoop's nested module flows:
 *   Dashboard → Family Health → Children's Health → Add Child
 *   "Back" on Add Child should return to Children's Health,
 *   not skip two levels back to Dashboard.
 *
 * react-router v6 marks the first navigation in a tab with
 * location.key === 'default' — that's our "nothing to pop" signal.
 *
 * Usage:
 *   const handleBack = useSafeBack('/family-health');
 *   <button onClick={handleBack}>Back</button>
 */
export const useSafeBack = (fallbackPath = '/dashboard') => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    if (location.key && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  }, [navigate, location.key, fallbackPath]);
};

export default useSafeBack;
